/**
 * Reusable GSAP + ScrollTrigger image glitch.
 *
 * Usage:
 *   createImageGlitch(elementOrSelector, options)
 *   createImageGlitchAll('[data-image-glitch]', options)
 *
 * Markup:
 *   <img data-image-glitch src="...">
 *   <img data-image-glitch data-glitch-intensity="16" data-glitch-once="false">
 */
(function (window, document) {
  'use strict';

  var STYLE_ID = 'cus-image-glitch-styles';
  var DEFAULTS = {
    start: 'top 80%',
    end: 'bottom 20%',
    once: true,
    duration: 0.55,
    intensity: 14,
    slices: 6,
    scramble: 7,
    colors: true,
  };

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.image-glitch-host{position:relative;overflow:hidden;isolation:isolate;}' +
      '.image-glitch-layer{position:absolute;inset:0;width:100%;height:100%;max-width:none;pointer-events:none;opacity:0;will-change:transform,clip-path,opacity,filter;}' +
      '.image-glitch-layer--rgb{mix-blend-mode:screen;}' +
      '.image-glitch-layer--red{filter:url(#cus-glitch-red);}' +
      '.image-glitch-layer--cyan{filter:url(#cus-glitch-cyan);}';
    document.head.appendChild(style);

    if (!document.getElementById('cus-image-glitch-filters')) {
      var wrap = document.createElement('div');
      wrap.id = 'cus-image-glitch-filters';
      wrap.setAttribute('aria-hidden', 'true');
      wrap.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
      wrap.innerHTML =
        '<svg>' +
        '<filter id="cus-glitch-red"><feColorMatrix type="matrix" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"/></filter>' +
        '<filter id="cus-glitch-cyan"><feColorMatrix type="matrix" values="0 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1 0"/></filter>' +
        '</svg>';
      document.body.appendChild(wrap);
    }
  }

  function toElements(target) {
    if (!target) return [];
    if (typeof target === 'string') return Array.prototype.slice.call(document.querySelectorAll(target));
    if (target instanceof Element) return [target];
    if (target.length) return Array.prototype.slice.call(target);
    return [];
  }

  function findImage(el) {
    if (!el) return null;
    if (el.tagName === 'IMG') return el;
    return el.querySelector('img');
  }

  function parseBool(value, fallback) {
    if (value == null || value === '') return fallback;
    return value === true || value === 'true' || value === '';
  }

  function readOptions(img, options) {
    var dataset = img.dataset || {};
    return {
      start: dataset.glitchStart || options.start || DEFAULTS.start,
      end: dataset.glitchEnd || options.end || DEFAULTS.end,
      once: dataset.glitchOnce != null ? parseBool(dataset.glitchOnce, DEFAULTS.once) : options.once != null ? options.once : DEFAULTS.once,
      duration: Number(dataset.glitchDuration || options.duration || DEFAULTS.duration),
      intensity: Number(dataset.glitchIntensity || options.intensity || DEFAULTS.intensity),
      slices: Number(dataset.glitchSlices || options.slices || DEFAULTS.slices),
      scramble: Number(dataset.glitchScramble || options.scramble || DEFAULTS.scramble),
      colors: dataset.glitchColors != null ? parseBool(dataset.glitchColors, true) : options.colors != null ? options.colors : DEFAULTS.colors,
      trigger: options.trigger || img,
    };
  }

  function prepareHost(img) {
    var host = img.parentElement;
    if (!host) return null;

    host.classList.add('image-glitch-host');

    var position = window.getComputedStyle(host).position;
    if (position === 'static') host.style.position = 'relative';
    host.style.overflow = 'hidden';

    return host;
  }

  function whenImageReady(img, callback) {
    if (img.complete && img.naturalWidth) {
      callback();
      return;
    }
    img.addEventListener('load', callback, { once: true });
  }

  function cloneImage(img, extraClass) {
    var clone = img.cloneNode(true);
    clone.className = (img.className + ' image-glitch-layer ' + (extraClass || '')).trim();
    clone.removeAttribute('data-image-glitch');
    clone.removeAttribute('id');
    clone.removeAttribute('loading');
    clone.setAttribute('aria-hidden', 'true');
    clone.src = img.currentSrc || img.src;
    clone.style.objectFit = window.getComputedStyle(img).objectFit || 'cover';
    clone.style.objectPosition = window.getComputedStyle(img).objectPosition || 'center';
    img.insertAdjacentElement('afterend', clone);
    return clone;
  }

  function buildLayers(img, opts) {
    if (img._glitchLayers) return img._glitchLayers;

    var slices = [];
    var i;
    for (i = 0; i < opts.slices; i += 1) {
      slices.push(cloneImage(img, 'image-glitch-layer--slice'));
    }

    var red = opts.colors ? cloneImage(img, 'image-glitch-layer--rgb image-glitch-layer--red') : null;
    var cyan = opts.colors ? cloneImage(img, 'image-glitch-layer--rgb image-glitch-layer--cyan') : null;

    img._glitchLayers = { slices: slices, red: red, cyan: cyan };
    return img._glitchLayers;
  }

  function playGlitch(img, layers, opts) {
    var gsap = window.gsap;
    var intensity = opts.intensity;
    var sliceCount = layers.slices.length;
    var tl = gsap.timeline();
    var i;
    var y1;
    var y2;
    var sliceHeight;

    gsap.killTweensOf([img].concat(layers.slices, layers.red || [], layers.cyan || []));

    tl.to(
      img,
      {
        duration: 0.05,
        x: gsap.utils.random(-intensity * 0.35, intensity * 0.35),
        skewX: gsap.utils.random(-4, 4),
        filter: 'contrast(1.35) saturate(1.45)',
        ease: 'none',
      },
      0
    );

    for (i = 0; i < sliceCount; i += 1) {
      sliceHeight = 100 / sliceCount;
      y1 = i * sliceHeight;
      y2 = 100 - (i + 1) * sliceHeight;

      gsap.set(layers.slices[i], {
        opacity: 1,
        x: 0,
        clipPath: 'inset(' + y1 + '% 0 ' + y2 + '% 0)',
      });

      tl.to(
        layers.slices[i],
        {
          x: function () {
            return gsap.utils.random(-intensity, intensity);
          },
          duration: opts.duration / opts.scramble,
          repeat: opts.scramble,
          yoyo: true,
          ease: 'steps(1)',
        },
        0
      );
    }

    if (layers.red && layers.cyan) {
      gsap.set([layers.red, layers.cyan], { opacity: 0.7, x: 0 });
      tl.to(
        layers.red,
        {
          x: -intensity,
          duration: 0.08,
          repeat: 4,
          yoyo: true,
          ease: 'none',
        },
        0
      );
      tl.to(
        layers.cyan,
        {
          x: intensity,
          duration: 0.08,
          repeat: 4,
          yoyo: true,
          ease: 'none',
        },
        0
      );
    }

    tl.to(
      layers.slices.concat(layers.red || [], layers.cyan || []),
      {
        x: 0,
        opacity: 0,
        duration: 0.12,
        ease: 'power2.out',
      },
      opts.duration * 0.72
    );

    tl.to(
      img,
      {
        x: 0,
        skewX: 0,
        filter: 'none',
        duration: 0.16,
        ease: 'power2.out',
      },
      '<'
    );

    return tl;
  }

  function createImageGlitch(target, options) {
    options = options || {};

    if (!window.gsap || !window.ScrollTrigger) {
      console.warn('createImageGlitch requires GSAP and ScrollTrigger');
      return [];
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return [];

    window.gsap.registerPlugin(window.ScrollTrigger);
    injectStyles();

    var instances = [];
    var elements = toElements(target);

    elements.forEach(function (el) {
      var img = findImage(el);
      if (!img || img._glitchBound) return;

      var opts = readOptions(img, options);
      var host = prepareHost(img);
      if (!host) return;

      img._glitchBound = true;

      var run = function () {
        playGlitch(img, buildLayers(img, opts), opts);
      };

      var trigger = window.ScrollTrigger.create({
        trigger: opts.trigger === img ? host : opts.trigger,
        start: opts.start,
        end: opts.end,
        once: opts.once,
        onEnter: function () {
          whenImageReady(img, run);
        },
        onEnterBack: opts.once
          ? undefined
          : function () {
              whenImageReady(img, run);
            },
      });

      instances.push({
        img: img,
        trigger: trigger,
        play: run,
        destroy: function () {
          trigger.kill();
          img._glitchBound = false;
        },
      });
    });

    return instances;
  }

  function createImageGlitchAll(selector, options) {
    return createImageGlitch(selector || '[data-image-glitch]', options || {});
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    createImageGlitchAll(scope.querySelectorAll('[data-image-glitch]'));
  }

  window.createImageGlitch = createImageGlitch;
  window.createImageGlitchAll = createImageGlitchAll;

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})(window, document);
